import { getSessionAuthentication, rms2BasePath } from '../common/Api';

interface FetchOptions {
    method: string;
    headers: Record<string, string>;
    body?: string;
}

function getHeaders(): Record<string, string> {
    const auth = getSessionAuthentication();
    return {
        'Content-Type': 'application/json',
        'Authorization': auth.token,
        'x-cleverapps-tenant': auth.tenantStringIdentifier,
    };
}

async function rms2Fetch<T>(path: string, options?: Partial<FetchOptions>): Promise<T> {
    const response = await fetch(rms2BasePath + path, {
        method: options?.method ?? 'GET',
        headers: getHeaders(),
        body: options?.body,
    });
    if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
    const text = await response.text();
    return text ? JSON.parse(text) : null;
}

// Types

export interface Pricelist {
    id: number;
    pricelistCode: string;
    pricelistName: string;
    currency: Currency | null;
    salesChannel: SalesChannel | null;
    customerGroup: CustomerGroup | null;
    priceFactor: number;
    exchangeRate: number;
    leastNetProfitPercentage: number;
    psychologicalPrice: PsychologicalPrice | null;
}

export interface Currency {
    id: number;
    code: string;
    currencyRate: number;
}

export interface SalesChannel {
    id: number;
    salesChannelCode: string;
    salesChannelDescription: string;
}

export interface CustomerGroup {
    id: number;
    code: string;
    name: string;
}

export interface PsychologicalPrice {
    id: number;
    name: string;
}

// API calls

export async function getPricelistBrief(id: number): Promise<Pricelist> {
    return rms2Fetch<Pricelist>(`pricelists/${id}`);
}

export async function getCurrencies(): Promise<Currency[]> {
    const response = await rms2Fetch<{ currencies: { id: number; code: string; currencyRate: number }[] }>('currencies');
    return response.currencies.map(c => ({ id: c.id, code: c.code, currencyRate: c.currencyRate }));
}

export async function getSalesChannels(): Promise<SalesChannel[]> {
    const response = await rms2Fetch<{ salesChannels: SalesChannel[] }>('saleschannels');
    return response.salesChannels;
}

export async function getCustomerGroups(): Promise<CustomerGroup[]> {
    const response = await rms2Fetch<{ customerGroups: CustomerGroup[] }>('customergroups');
    return response.customerGroups;
}

export async function getPsychologicalPrices(): Promise<PsychologicalPrice[]> {
    const response = await rms2Fetch<{ psychologicalPrices: PsychologicalPrice[] }>('psychologicalprices');
    return response.psychologicalPrices;
}

export async function createPricelist(pricelist: Partial<Pricelist>): Promise<Pricelist> {
    return rms2Fetch<Pricelist>('pricelists', {
        method: 'POST',
        body: JSON.stringify({
            pricelistCode: pricelist.pricelistCode,
            pricelistName: pricelist.pricelistName,
            currencyId: pricelist.currency?.id ?? 0,
            salesChannelId: pricelist.salesChannel?.id || null,
            customerGroupId: pricelist.customerGroup?.id || null,
            priceFactor: pricelist.priceFactor ?? 0,
            exchangeRate: pricelist.exchangeRate ?? 0,
            leastNetProfitPercentage: pricelist.leastNetProfitPercentage ?? 0,
            psychologicalPriceId: pricelist.psychologicalPrice?.id || null,
        }),
    });
}

export async function updatePricelist(pricelist: Pricelist): Promise<Pricelist> {
    return rms2Fetch<Pricelist>(`pricelists/${pricelist.id}`, {
        method: 'PUT',
        body: JSON.stringify({
            pricelistCode: pricelist.pricelistCode,
            pricelistName: pricelist.pricelistName,
            currencyId: pricelist.currency?.id ?? 0,
            salesChannelId: pricelist.salesChannel?.id || null,
            customerGroupId: pricelist.customerGroup?.id || null,
            priceFactor: pricelist.priceFactor,
            exchangeRate: pricelist.exchangeRate,
            leastNetProfitPercentage: pricelist.leastNetProfitPercentage,
            psychologicalPriceId: pricelist.psychologicalPrice?.id || null,
        }),
    });
}
